import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { CONTACT } from "@/lib/data";
import WhatsAppButton from "@/components/WhatsAppButton";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/products", label: "Terrazzo" },
  { to: "/bricks", label: "Bricks" },
  { to: "/work", label: "Work" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => { setOpen(false); window.scrollTo(0, 0); }, [pathname]);

  return (
    <div className="bg-cream text-ink min-h-screen flex flex-col">
      {/* Header */}
      <header
        data-testid="site-header"
        className="sticky top-0 z-50 bg-ink text-cream border-b border-cream/10"
      >
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-16 lg:px-20 py-3 sm:py-3.5 flex items-center justify-between gap-3">
          <Link to="/" data-testid="site-logo" className="flex items-center group shrink-0" aria-label="Terrazzo India — Designed by you, Crafted by us">
            <img
              src="/brand/logo-full.png"
              alt="Terrazzo India — Designed by you, Crafted by us"
              className="h-14 sm:h-16 md:h-[68px] lg:h-20 w-auto select-none"
              draggable="false"
            />
          </Link>

          <nav className="hidden md:flex items-center gap-5 lg:gap-8 xl:gap-10">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `font-mono-label transition-colors hover:text-terracotta ${
                    isActive ? "text-terracotta" : "text-cream/85"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/request-quote"
              data-testid="nav-request-quote"
              className="font-mono-label px-4 lg:px-5 py-2.5 lg:py-3 bg-terracotta text-cream hover:bg-cream hover:text-ink transition-colors whitespace-nowrap"
            >
              Request a Sample →
            </Link>
          </nav>

          <button
            data-testid="nav-mobile-toggle"
            className="md:hidden font-mono-label text-cream"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? "Close" : "Menu"}
          </button>
        </div>

        {/* Mobile menu */}
        {open && (
          <div data-testid="nav-mobile-menu" className="md:hidden bg-ink border-t border-cream/10">
            <div className="px-4 sm:px-6 py-6 flex flex-col gap-4">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-mobile-${n.label.toLowerCase()}`}
                  className="font-serif-display text-3xl sm:text-4xl text-cream hover:text-terracotta py-1"
                >
                  {n.label}
                </NavLink>
              ))}
              <Link
                to="/request-quote"
                data-testid="nav-mobile-request-quote"
                className="font-mono-label inline-block px-5 py-3 bg-terracotta text-cream w-fit mt-2"
              >
                Request a Sample →
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 overflow-x-hidden">{children}</main>

      {/* Footer */}
      <footer data-testid="site-footer" className="bg-ink text-cream mt-0">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-16 lg:px-20 py-14 sm:py-16 md:py-20 grid grid-cols-12 gap-8 sm:gap-10">
          <div className="col-span-12 md:col-span-5">
            <img
              src="/brand/logo-full.png"
              alt="Terrazzo India — Designed by you, Crafted by us"
              className="h-20 sm:h-24 md:h-28 w-auto mb-6 sm:mb-7 select-none"
              draggable="false"
            />
            <p className="font-mono-label text-cream/60">A Yogii Industries brand · Est. 1999</p>
            <h3 className="font-serif-display text-3xl sm:text-4xl md:text-5xl mt-4 sm:mt-5 leading-tight">
              What you <em className="italic text-terracotta">imagine</em><br/>is what gets delivered.
            </h3>
          </div>
          <div className="col-span-12 sm:col-span-6 md:col-span-3">
            <p className="font-mono-label text-cream/60 mb-3 sm:mb-4">Studio</p>
            <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm leading-relaxed">
              {CONTACT.address.map((l) => <li key={l}>{l}</li>)}
            </ul>
            <p className="mt-4 sm:mt-5 text-xs sm:text-sm text-cream/70">{CONTACT.hours}</p>
          </div>
          <div className="col-span-12 sm:col-span-6 md:col-span-2">
            <p className="font-mono-label text-cream/60 mb-3 sm:mb-4">Reach</p>
            <a href={CONTACT.phoneHref} className="block text-xs sm:text-sm hover:text-terracotta break-all">{CONTACT.phone}</a>
            <a href={`mailto:${CONTACT.email}`} className="block text-xs sm:text-sm hover:text-terracotta mt-2 break-all">{CONTACT.email}</a>
            <a
              href={CONTACT.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram"
              className="mt-4 inline-flex items-center gap-2 text-xs sm:text-sm hover:text-terracotta group"
              aria-label="Follow Terrazzo India on Instagram"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 shrink-0 transition-transform group-hover:scale-110">
                <rect x="3" y="3" width="18" height="18" rx="5" />
                <circle cx="12" cy="12" r="4" />
                <circle cx="17.5" cy="6.5" r="0.9" fill="currentColor" />
              </svg>
              <span className="break-all">{CONTACT.instagramHandle}</span>
            </a>
          </div>
          <div className="col-span-12 md:col-span-2 md:text-right">
            <p className="font-mono-label text-cream/60 mb-3 sm:mb-4">Index</p>
            <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
              {NAV.map((n) => (
                <li key={n.to}><Link to={n.to} className="hover:text-terracotta">{n.label}</Link></li>
              ))}
              <li><Link to="/request-quote" className="hover:text-terracotta">Request a sample</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-cream/10">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-16 lg:px-20 py-5 sm:py-6 flex flex-col md:flex-row justify-between gap-2 sm:gap-3">
            <p className="font-mono-label text-cream/50 text-[0.65rem] sm:text-xs">© {new Date().getFullYear()} Terrazzo India</p>
            <p className="font-mono-label text-cream/50 text-[0.65rem] sm:text-xs">Vasai · Maharashtra · India</p>
          </div>
        </div>
      </footer>
      <WhatsAppButton />
    </div>
  );
}
